import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  CALLABLE_FUNCTIONS,
  type ActiveEncodeMode,
  type EncodingPreference,
  type EndSessionRequest,
  type EndSessionResponse,
  type QualityMode,
  type SessionDocument,
} from "@srd/shared";
import { db, functions } from "../firebase";
import {
  clearSessionSecrets,
  loadSessionSecrets,
  type StashedSessionSecrets,
} from "../session/sessionSecrets";
import { SessionStatusBar } from "../session/SessionStatusBar";
import { QualityControls } from "../session/QualityControls";
import { FileTransferPanel, type FileTransferEvent } from "../session/FileTransferPanel";
import { DesktopSurface } from "../session/DesktopSurface";
import {
  ViewerSession,
  type ViewerConnectionState,
} from "../webrtc/viewerSession";
import type { CompositorStats, CursorState } from "../webrtc/tileCompositor";

function preferenceToQualityMode(pref: EncodingPreference): QualityMode {
  if (pref === "h264_hw") return "video_smooth";
  if (pref === "ui_tile") return "ui_sharp";
  return "auto";
}

export function SessionPage() {
  const { sessionId = "" } = useParams();
  const navigate = useNavigate();
  const [secrets, setSecrets] = useState<StashedSessionSecrets | null>(null);
  const [sessionDoc, setSessionDoc] = useState<SessionDocument | null>(null);
  const [connection, setConnection] = useState<ViewerConnectionState>("idle");
  const [stats, setStats] = useState<CompositorStats | null>(null);
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const [encodeMode, setEncodeMode] = useState<ActiveEncodeMode | null>(null);
  const [hasVideoTrack, setHasVideoTrack] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileEvents, setFileEvents] = useState<FileTransferEvent[]>([]);
  const [quality, setQuality] = useState({
    qualityMode: "ui_sharp" as QualityMode,
    encodingPreference: "ui_tile" as EncodingPreference,
    maxFps: 15,
    maxWidth: 1920,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const viewerRef = useRef<ViewerSession | null>(null);
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    const loaded = loadSessionSecrets(sessionId);
    if (!loaded) {
      setError("Missing in-memory session secrets. Start again from Devices.");
      return;
    }
    setSecrets(loaded);
    setQuality((q) => ({
      ...q,
      encodingPreference: loaded.encodingPreference,
      qualityMode: preferenceToQualityMode(loaded.encodingPreference),
    }));
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    return onSnapshot(
      doc(db, "sessions", sessionId),
      (snap) => {
        if (!snap.exists()) {
          setError("Session document missing.");
          return;
        }
        setSessionDoc(snap.data() as SessionDocument);
      },
      (err) => setError(err.message),
    );
  }, [sessionId]);

  useEffect(() => {
    if (!secrets || !sessionDoc) return;
    if (sessionDoc.status !== "active") return;
    if (!canvasRef.current || !videoRef.current) return;
    if (startedFor.current === secrets.sessionId) return;

    startedFor.current = secrets.sessionId;
    const viewer = new ViewerSession({
      sessionId: secrets.sessionId,
      sessionJwt: secrets.sessionJwt,
      turn: secrets.turn,
      canvas: canvasRef.current,
      video: videoRef.current,
      preferredMode: preferenceToQualityMode(secrets.encodingPreference),
      encodingPreference: secrets.encodingPreference,
      onState: setConnection,
      onStats: setStats,
      onCursor: setCursor,
      onEncodeMode: setEncodeMode,
      onRemoteTrack: setHasVideoTrack,
      onError: (message) => setError(message),
      onFileEvent: (event) => setFileEvents((prev) => [...prev, event]),
    });
    viewerRef.current = viewer;
    void viewer.start().catch((err) => setError(String(err)));

    return () => {
      void viewer.close();
      viewerRef.current = null;
      startedFor.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secrets, sessionDoc?.status]);

  async function endSession() {
    try {
      await viewerRef.current?.close("viewer_hangup");
      const callable = httpsCallable<EndSessionRequest, EndSessionResponse>(
        functions,
        CALLABLE_FUNCTIONS.endSession,
      );
      await callable({ sessionId, reason: "viewer_hangup" });
    } catch (err) {
      setError(String(err));
    } finally {
      clearSessionSecrets();
      navigate("/devices");
    }
  }

  const waitingAccept = sessionDoc?.status === "pending_accept";
  const showVideo =
    hasVideoTrack &&
    (encodeMode === "h264_hw" ||
      encodeMode === "h264_sw" ||
      quality.encodingPreference === "h264_hw" ||
      quality.qualityMode === "video_smooth");
  const inputEnabled = connection === "connected" && sessionDoc?.status === "active";

  return (
    <div className="page session-page">
      <header className="topbar">
        <div>
          <h1>{secrets?.deviceName ?? "Session"}</h1>
          <p className="muted tiny">
            {sessionId} · {secrets?.mode ?? "—"} · grant expires{" "}
            {secrets ? new Date(secrets.expiresAt).toLocaleTimeString() : "—"}
          </p>
        </div>
        <div className="row">
          <Link className="button-link" to="/devices">
            Devices
          </Link>
          <button type="button" onClick={() => void viewerRef.current?.refreshTurn()}>
            Refresh TURN
          </button>
          <button type="button" className="danger" onClick={() => void endSession()}>
            End session
          </button>
        </div>
      </header>

      <SessionStatusBar
        sessionStatus={sessionDoc?.status ?? "…"}
        connection={connection}
        encodeMode={encodeMode}
        stats={stats}
        hasVideoTrack={hasVideoTrack}
      />

      {waitingAccept ? (
        <section className="panel">
          <p className="status">
            Waiting for customer Accept on the agent… WebRTC starts when status becomes
            active.
          </p>
        </section>
      ) : null}

      {sessionDoc?.recordingConsentRequested ? (
        <section className="panel">
          <h2>Recording consent</h2>
          <p className="status">
            {sessionDoc.recordingConsentGranted === true
              ? `Granted (${sessionDoc.recordingConsentSource ?? "—"}${
                  sessionDoc.recordingConsentAt
                    ? ` · ${new Date(sessionDoc.recordingConsentAt).toLocaleString()}`
                    : ""
                })`
              : sessionDoc.recordingConsentGranted === false
                ? "Declined or not requested for capture"
                : "Requested — waiting for customer/agent decision (no media muxer yet)"}
          </p>
          <p className="muted tiny">
            Encrypted Storage path:{" "}
            <code>
              recordings/{sessionDoc.tenantId}/{sessionId}/
            </code>{" "}
            — see docs/session-recording.md
          </p>
        </section>
      ) : null}

      <div className="session-layout">
        <DesktopSurface
          sessionRef={viewerRef}
          canvasRef={canvasRef}
          videoRef={videoRef}
          showVideo={showVideo}
          cursor={cursor}
          inputEnabled={inputEnabled}
        />
        <div className="session-side">
          <QualityControls
            qualityMode={quality.qualityMode}
            encodingPreference={quality.encodingPreference}
            maxFps={quality.maxFps}
            maxWidth={quality.maxWidth}
            disabled={!inputEnabled}
            onChange={setQuality}
            onApply={() => {
              viewerRef.current?.sendQuality(quality.qualityMode, {
                maxFps: quality.maxFps,
                maxWidth: quality.maxWidth,
              });
              viewerRef.current?.sendModeSwitch(quality.encodingPreference);
            }}
          />
          <FileTransferPanel
            disabled={!inputEnabled}
            events={fileEvents}
            onUpload={async (file) => {
              await viewerRef.current?.uploadFile(file);
            }}
          />
        </div>
      </div>

      {error ? <p className="status err">{error}</p> : null}
    </div>
  );
}
